import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { reportsApi } from "../../services/api";
import { PageHeader, Badge } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  Users,
  Wallet,
  Receipt,
  Moon,
  ShieldCheck,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Download,
  Trash2,
  Calendar,
  Check,
  BarChart2,
  ClipboardList,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  AlertTriangle,
  X,
  FileSpreadsheet,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { value: "USERS",         label: "App Users",    Icon: Users,         desc: "All registered users with profile info" },
  { value: "WALLETS",       label: "Wallet Txns",  Icon: Wallet,        desc: "All wallet credit/debit transactions" },
  { value: "CREDIT_ORDERS", label: "Credit Orders",Icon: Receipt,       desc: "All Razorpay credit purchase orders" },
  { value: "HOROSCOPE",     label: "Horoscopes",   Icon: Moon,          desc: "Cached horoscope entries" },
  { value: "ADMINS",        label: "Admin Users",  Icon: ShieldCheck,   desc: "All admin accounts and their roles" },
];

const STATUS_OPTIONS = [
  { value: "",        label: "All Statuses" },
  { value: "SUCCESS", label: "Success only" },
  { value: "CREATED", label: "Pending only" },
  { value: "FAILED",  label: "Failed only"  },
];

const STATUS_DISPLAY = {
  CREATED: "Pending",
  SUCCESS: "Success",
  FAILED:  "Failed",
};

const TYPE_META = {
  USERS:         { bg: "rgba(99,102,241,0.1)",  color: "#4f46e5", border: "rgba(99,102,241,0.2)"  },
  WALLETS:       { bg: "rgba(16,185,129,0.1)",  color: "#059669", border: "rgba(16,185,129,0.2)"  },
  CREDIT_ORDERS: { bg: "rgba(245,158,11,0.1)",  color: "#d97706", border: "rgba(245,158,11,0.2)"  },
  KUNDLI:        { bg: "rgba(236,72,153,0.1)",  color: "#db2777", border: "rgba(236,72,153,0.2)"  },
  HOROSCOPE:     { bg: "rgba(139,92,246,0.1)",  color: "#7c3aed", border: "rgba(139,92,246,0.2)"  },
  ADMINS:        { bg: "rgba(59,130,246,0.1)",  color: "#2563eb", border: "rgba(59,130,246,0.2)"  },
  COMPATIBILITY: { bg: "rgba(239,68,68,0.1)",   color: "#dc2626", border: "rgba(239,68,68,0.2)"   },
};

const STATUS_META = {
  QUEUED:     { bg: "rgba(245,158,11,0.1)",  color: "#d97706", border: "rgba(245,158,11,0.2)"  },
  PROCESSING: { bg: "rgba(59,130,246,0.1)",  color: "#2563eb", border: "rgba(59,130,246,0.2)"  },
  DONE:       { bg: "rgba(16,185,129,0.1)",  color: "#059669", border: "rgba(16,185,129,0.2)"  },
  FAILED:     { bg: "rgba(239,68,68,0.1)",   color: "#dc2626", border: "rgba(239,68,68,0.2)"   },
};

const MS_24H = 24 * 60 * 60 * 1000;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTimestamp(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function isOlderThan24h(ts) {
  if (!ts) return false;
  return Date.now() - new Date(ts).getTime() > MS_24H;
}

function timeUntilExpiry(ts) {
  if (!ts) return null;
  const remaining = MS_24H - (Date.now() - new Date(ts).getTime());
  if (remaining <= 0) return "Expired";
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  return `${h}h ${m}m left`;
}

// ─── Badge components ─────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const meta = TYPE_META[type] || { bg: "rgba(107,114,128,0.1)", color: "#6b7280", border: "rgba(107,114,128,0.2)" };
  const rt   = REPORT_TYPES.find(r => r.value === type);
  const Icon = rt?.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
      whiteSpace: "nowrap",
    }}>
      {Icon && <Icon size={11} strokeWidth={2} />}
      {rt?.label || type?.replace(/_/g, " ")}
    </span>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { bg: "rgba(107,114,128,0.1)", color: "#6b7280", border: "rgba(107,114,128,0.2)" };
  const displayLabel = STATUS_DISPLAY[status] || status;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
      whiteSpace: "nowrap",
    }}>
      {displayLabel}
    </span>
  );
}

function ProgressBar({ pct, status }) {
  const color =
    status === "DONE"       ? "#059669" :
    status === "FAILED"     ? "#dc2626" :
    status === "PROCESSING" ? "#2563eb" : "#d97706";
  return (
    <div style={{ height: 6, background: "var(--border)", borderRadius: 3, overflow: "hidden", minWidth: 80 }}>
      <div style={{
        height: "100%", width: `${Math.min(100, pct || 0)}%`,
        background: color, borderRadius: 3, transition: "width 0.4s ease",
      }} />
    </div>
  );
}

// ─── Column header with dropdown filter ──────────────────────────────────────
function FilterHeader({ label, value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0 });
  const btnRef   = useRef(null);
  const panelRef = useRef(null);
  const active   = !!value;

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (
        btnRef.current   && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX });
    }
    setOpen(prev => !prev);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        style={{
          display: "flex", alignItems: "center", gap: 5,
          background: "none", border: "none", cursor: "pointer", padding: 0,
          fontSize: 10, fontWeight: 700,
          color: active ? "#0F3D3E" : "var(--text-muted)",
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}
      >
        {label}
        {open
          ? <ChevronUp size={9} strokeWidth={3} />
          : <ChevronDown size={9} strokeWidth={3} />
        }
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: pos.top, left: pos.left,
            zIndex: 99999, background: "#0F3D3E",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
            minWidth: 160, overflow: "hidden",
          }}
        >
          {options.map((opt) => {
            const isActive = value === opt.value;
            const Icon = opt.Icon;
            return (
              <div
                key={String(opt.value)}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  padding: "9px 14px", cursor: "pointer", fontSize: 12, fontWeight: 500,
                  color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                  background: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                  transition: "background 0.15s",
                  display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                {isActive && <Check size={10} strokeWidth={3} color="#fff" />}
                {Icon && <Icon size={12} strokeWidth={2} />}
                {opt.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Sortable column header ───────────────────────────────────────────────────
function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  const SortIcon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button onClick={() => onSort(field)} style={{
      display: "flex", alignItems: "center", gap: 5,
      background: "none", border: "none", cursor: "pointer", padding: 0,
      fontSize: 10, fontWeight: 700,
      color: active ? "#0F3D3E" : "var(--text-muted)",
      textTransform: "uppercase", letterSpacing: "0.5px",
    }}>
      {label}
      <SortIcon size={9} strokeWidth={3} style={{ opacity: active ? 1 : 0.35 }} />
    </button>
  );
}

const inputStyle = {
  padding: "8px 12px", background: "var(--bg-input)", border: "1px solid var(--border)",
  borderRadius: "var(--radius)", color: "var(--text-primary)", fontSize: 13,
  outline: "none", fontFamily: "var(--font-body)", transition: "border-color 0.18s",
};

const labelStyle = {
  fontSize: 11, fontWeight: 600, color: "var(--text-muted)",
  textTransform: "uppercase", letterSpacing: "0.5px",
  display: "block", marginBottom: 5,
};

// ─── Date preset helper ───────────────────────────────────────────────────────
const DATE_PRESETS = [
  { label: "Today",      value: "TODAY"      },
  { label: "Yesterday",  value: "YESTERDAY"  },
  { label: "This Week",  value: "THIS_WEEK"  },
  { label: "This Month", value: "THIS_MONTH" },
  { label: "All Time",   value: "ALL_TIME"   },
];

function getPresetDates(preset) {
  const now   = new Date();
  const pad   = n => String(n).padStart(2, "0");
  const fmt   = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = fmt(now);

  if (preset === "TODAY")      return { fromDate: today,   toDate: today };
  if (preset === "YESTERDAY") {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    return { fromDate: fmt(y), toDate: fmt(y) };
  }
  if (preset === "THIS_WEEK") {
    const start = new Date(now); start.setDate(now.getDate() - now.getDay());
    return { fromDate: fmt(start), toDate: today };
  }
  if (preset === "THIS_MONTH") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { fromDate: fmt(start), toDate: today };
  }
  return { fromDate: "", toDate: "" };
}

const getTodayDate = () => {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Reports() {
  const [form,           setForm]           = useState({ reportType: "USERS", fromDate: "", toDate: "", filterStatus: "" });
  const [activePreset,   setActivePreset]   = useState("ALL_TIME");
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [tempFrom,       setTempFrom]       = useState("");
  const [tempTo,         setTempTo]         = useState("");
  const [generating,     setGenerating]     = useState(false);
  const [downloadingId,  setDownloadingId]  = useState(null);
  const [pendingDeleteId,setPendingDeleteId]= useState(null);

  const [jobs,           setJobs]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const { isSuper }  = useAuth();
  const isSuperAdmin = isSuper;
  const [page,           setPage]           = useState(0);
  const [totalPages,     setTotalPages]     = useState(0);
  const [totalElements,  setTotalElements]  = useState(0);

  const [typeFilter,   setTypeFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField,    setSortField]    = useState("createdAt");
  const [sortDir,      setSortDir]      = useState("desc");

  const pollRef        = useRef(null);
  const dateDropdownRef = useRef(null);
  const maxDate = getTodayDate();

  const handleDownload = async (job) => {
    if (!job.downloadUrl) return;
    setDownloadingId(job.id);
    try {
      const response = await fetch(job.downloadUrl);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();

      const reportLabel = REPORT_TYPES.find(r => r.value === job.reportType)?.label || job.reportType;
      const dateStr     = job.createdAt ? new Date(job.createdAt).toISOString().slice(0, 10) : "report";
      const fileName    = `${reportLabel.replace(/\s+/g, "_")}_${dateStr}.xlsx`;

      if (window.showSaveFilePicker) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: fileName,
            types: [{ description: "Excel Spreadsheet", accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] } }],
          });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          showToast(`${reportLabel} report saved successfully!`, "success");
          return;
        } catch (pickerErr) {
          if (pickerErr.name === "AbortError") return;
        }
      }

      const blobUrl = URL.createObjectURL(blob);
      const a       = document.createElement("a");
      a.href        = blobUrl;
      a.download    = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
      showToast(`Downloading ${reportLabel} report…`, "success");
    } catch {
      showToast("Download failed. Please try again.", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchJobs = useCallback(async (overridePage) => {
    setLoading(true);
    const p = overridePage !== undefined ? overridePage : page;
    try {
      if (isSuperAdmin) {
        const r   = await reportsApi.getAllJobs(p, 20);
        const raw = r.data.data;
        setJobs(raw?.content || []);
        setTotalPages(raw?.totalPages || 0);
        setTotalElements(raw?.totalElements || 0);
      } else {
        const r    = await reportsApi.getMyJobs();
        const list = r.data.data || [];
        setJobs(list);
        setTotalPages(1);
        setTotalElements(list.length);
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to load reports", "error");
    } finally {
      setLoading(false);
    }
  }, [page, isSuperAdmin]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // ── Poll active jobs every 2s ──────────────────────────────────────────────
  useEffect(() => {
    const active = jobs.filter(j => j.jobStatus === "QUEUED" || j.jobStatus === "PROCESSING");
    if (active.length === 0) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;

    pollRef.current = setInterval(async () => {
      let anyActive = false;
      const updated = [...jobs];
      for (const job of active) {
        try {
          const r     = await reportsApi.getJob(job.id);
          const fresh = r.data.data;
          const idx   = updated.findIndex(j => j.id === job.id);
          if (idx >= 0) updated[idx] = fresh;
          if (fresh.jobStatus === "QUEUED" || fresh.jobStatus === "PROCESSING") anyActive = true;
        } catch {}
      }
      setJobs([...updated]);
      if (!anyActive) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 2000);

    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobs]);

  useEffect(() => {
    const h = (e) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target))
        setDateDropdownOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Generate ───────────────────────────────────────────────────────────────
  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const r = await reportsApi.requestReport({
        reportType:   form.reportType,
        fromDate:     form.fromDate     || null,
        toDate:       form.toDate       || null,
        filterStatus: form.filterStatus || null,
      });
      showToast(`${form.reportType} report queued!`);
      setJobs(prev => [r.data.data, ...prev]);
      setForm(prev => ({ ...prev, fromDate: "", toDate: "", filterStatus: "" }));
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to generate report", "error");
    } finally {
      setGenerating(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (jobId) => {
    if (pendingDeleteId !== jobId) {
      setPendingDeleteId(jobId);
      showToast("Click delete again within 3 seconds to confirm", "error");
      setTimeout(() => {
        setPendingDeleteId((curr) => (curr === jobId ? null : curr));
      }, 3000);
      return;
    }
    try {
      await reportsApi.deleteJob(jobId);
      setJobs(prev => prev.filter(j => j.id !== jobId));
      setPendingDeleteId(null);
      showToast("Report deleted", "success");
    } catch (err) {
      setPendingDeleteId(null);
      showToast(err.response?.data?.message || "Failed to delete", "error");
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  // ── Derived: filter → sort ─────────────────────────────────────────────────
  const visibleJobs = jobs
    .filter(j => {
      if (typeFilter   && j.reportType !== typeFilter)                        return false;
      if (statusFilter && (j.jobStatus || "").toUpperCase() !== statusFilter) return false;
      return true;
    })
    .filter(j => {
      if ((j.jobStatus === "DONE" || j.jobStatus === "FAILED") && isOlderThan24h(j.createdAt)) return false;
      return true;
    })
    .sort((a, b) => {
      let av, bv;
      if (sortField === "createdAt") {
        av = new Date(a.createdAt || 0).getTime();
        bv = new Date(b.createdAt || 0).getTime();
      } else if (sortField === "dateRange") {
        av = a.fromDate || "";
        bv = b.fromDate || "";
      } else {
        av = a[sortField] ?? "";
        bv = b[sortField] ?? "";
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ?  1 : -1;
      return 0;
    });

  const handlePageChange = (p) => { setPage(p); fetchJobs(p); };

  const typeOptions = [
    { value: "", label: "All Types" },
    ...REPORT_TYPES.map(r => ({ value: r.value, label: r.label, Icon: r.Icon })),
  ];

  const jobStatusOptions = [
    { value: "",           label: "All Status"  },
    { value: "QUEUED",     label: "Queued"      },
    { value: "PROCESSING", label: "Processing"  },
    { value: "DONE",       label: "Done"        },
    { value: "FAILED",     label: "Failed"      },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0, width: "100%", overflow: "hidden" }}>

      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Reports</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Generate and download Excel reports · history auto-clears after 24 hours
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {isSuperAdmin && (
            <span style={{
              padding: "6px 14px", borderRadius: "var(--radius)",
              fontSize: 12, fontWeight: 600,
              background: "rgba(15,61,62,0.08)", color: "#0F3D3E",
              border: "1px solid rgba(15,61,62,0.2)",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <ClipboardList size={13} strokeWidth={2} /> All Reports View
            </span>
          )}
          <button onClick={() => fetchJobs()} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
            borderRadius: "var(--radius)", fontSize: 12, fontWeight: 600,
            border: "1px solid var(--border)", cursor: "pointer",
            background: "var(--bg-card)", color: "var(--text-secondary)", transition: "all 0.15s",
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#0F3D3E"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
          >
            <RefreshCw size={13} strokeWidth={2.5} />
            Refresh
          </button>
        </div>
      </div>

      {/* Generate Report Form */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)", padding: "20px 24px", boxShadow: "var(--shadow-sm)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <div style={{ width: 4, height: 18, borderRadius: 2, background: "var(--primary)", flexShrink: 0 }} />
          <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--primary)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <FileSpreadsheet size={15} strokeWidth={2} /> Generate New Report
          </span>
        </div>

        <form onSubmit={handleGenerate}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Row 1 — Report Type + Submit */}
            <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>Report Type</label>
                <select
                  value={form.reportType}
                  onChange={e => setForm({ ...form, reportType: e.target.value, filterStatus: "" })}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "var(--primary)"}
                  onBlur={e  => e.target.style.borderColor = "var(--border)"}
                >
                  {REPORT_TYPES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {form.reportType === "CREDIT_ORDERS" && (
                <div style={{ flex: "1 1 160px" }}>
                  <label style={labelStyle}>Status Filter</label>
                  <select
                    value={form.filterStatus}
                    onChange={e => setForm({ ...form, filterStatus: e.target.value })}
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = "var(--primary)"}
                    onBlur={e  => e.target.style.borderColor = "var(--border)"}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ flex: "0 0 auto" }}>
                <button
                  type="submit"
                  disabled={generating}
                  style={{
                    height: 38, padding: "0 24px",
                    background: generating ? "rgba(var(--primary-rgb),0.5)" : "var(--primary)",
                    color: "#fff", border: "none", borderRadius: "var(--radius)",
                    fontSize: 13, fontWeight: 600,
                    cursor: generating ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: 6,
                    transition: "background 0.18s", whiteSpace: "nowrap",
                  }}
                >
                  {generating ? (
                    <><Loader2 size={14} strokeWidth={2.5} style={{ animation: "spin 1s linear infinite" }} /> Generating…</>
                  ) : (
                    <><Download size={14} strokeWidth={2.5} /> Generate Report</>
                  )}
                </button>
              </div>
            </div>

            {/* Row 2 — Date Range Split Dropdown */}
            <div>
              <label style={labelStyle}>Date Range</label>
              <div style={{ position: "relative" }} ref={dateDropdownRef}>

                {/* Trigger */}
                <button
                  type="button"
                  onClick={() => setDateDropdownOpen(o => !o)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    width: "100%", maxWidth: 320,
                    padding: "8px 12px", borderRadius: "var(--radius)",
                    border: `1px solid ${dateDropdownOpen ? "var(--primary)" : "var(--border)"}`,
                    background: "var(--bg-input)", color: "var(--text-primary)",
                    fontSize: 13, fontFamily: "var(--font-body)",
                    cursor: "pointer", outline: "none", transition: "border-color 0.18s", gap: 8,
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Calendar size={14} strokeWidth={2} />
                    {activePreset === "CUSTOM" && (form.fromDate || form.toDate)
                      ? `${form.fromDate || "start"} → ${form.toDate || "today"}`
                      : DATE_PRESETS.find(p => p.value === activePreset)?.label || "All Time"
                    }
                  </span>
                  {dateDropdownOpen
                    ? <ChevronUp size={12} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                    : <ChevronDown size={12} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                  }
                </button>

                {/* Split panel dropdown */}
                {dateDropdownOpen && (
                  <>
                    <div onClick={() => setDateDropdownOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                    <div style={{
                      position: "absolute", top: "calc(100% + 8px)", left: 0,
                      zIndex: 1000, display: "flex",
                      background: "var(--bg-card)",
                      border: "1px solid var(--border)",
                      borderRadius: 14,
                      boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
                      overflow: "hidden", minWidth: 520,
                    }}>

                      {/* LEFT — Presets */}
                      <div style={{ width: 160, borderRight: "1px solid var(--border)", padding: "8px 0", background: "var(--bg-input)" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", padding: "6px 16px 10px" }}>
                          Quick Select
                        </div>
                        {DATE_PRESETS.map(p => {
                          const isActive = activePreset === p.value;
                          return (
                            <div
                              key={p.value}
                              onClick={() => {
                                setActivePreset(p.value);
                                const dates = getPresetDates(p.value);
                                setForm(prev => ({ ...prev, fromDate: dates.fromDate, toDate: dates.toDate }));
                              }}
                              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "var(--border)"; }}
                              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                              style={{
                                padding: "9px 16px", cursor: "pointer",
                                fontSize: 13, fontWeight: isActive ? 700 : 500,
                                color: isActive ? "var(--primary)" : "var(--text-secondary)",
                                background: isActive ? "rgba(15,61,62,0.08)" : "transparent",
                                borderLeft: isActive ? "3px solid var(--primary)" : "3px solid transparent",
                                transition: "all 0.15s",
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                              }}
                            >
                              {p.label}
                              {isActive && <Check size={12} strokeWidth={3} color="var(--primary)" />}
                            </div>
                          );
                        })}
                      </div>

                      {/* RIGHT — Custom date pickers */}
                      <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>
                          Custom Date Range
                        </div>

                        <div style={{ display: "flex", gap: 14 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>From</div>
                            <input
                              type="date"
                              value={tempFrom}
                              max={maxDate}
                              onChange={(e) => {
                                setTempFrom(e.target.value);
                                setActivePreset('CUSTOM');
                              }}
                              style={{
                                width: '100%',
                                padding: '9px 12px',
                                background: 'var(--bg-input)',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                color: 'var(--text-primary)',
                                fontSize: 13,
                                outline: 'none',
                                fontFamily: 'var(--font-body)',
                              }}
                              onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                              onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>To</div>
                            <input
                              type="date"
                              value={tempTo}
                              max={maxDate}
                              onChange={(e) => {
                                setTempTo(e.target.value);
                                setActivePreset('CUSTOM');
                              }}
                              style={{
                                width: '100%',
                                padding: '9px 12px',
                                background: 'var(--bg-input)',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                color: 'var(--text-primary)',
                                fontSize: 13,
                                outline: 'none',
                                fontFamily: 'var(--font-body)',
                              }}
                              onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                              onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                            />
                          </div>
                        </div>

                        {activePreset !== "CUSTOM" && (
                          <div style={{ fontSize: 12, color: "var(--text-muted)", background: "var(--bg-input)", borderRadius: "var(--radius)", padding: "8px 12px", border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 6 }}>
                            <Calendar size={12} strokeWidth={2} />
                            Showing: <strong style={{ color: "var(--text-primary)" }}>{DATE_PRESETS.find(p => p.value === activePreset)?.label}</strong>
                            {" "}— or pick custom dates above to override
                          </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: "auto" }}>
                          <button
                            type="button"
                            onClick={() => {
                              setActivePreset("ALL_TIME");
                              setTempFrom(""); setTempTo("");
                              setForm(prev => ({ ...prev, fromDate: "", toDate: "" }));
                              setDateDropdownOpen(false);
                            }}
                            style={{ padding: "8px 18px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer" }}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (activePreset === "CUSTOM") setForm(prev => ({ ...prev, fromDate: tempFrom, toDate: tempTo }));
                              setDateDropdownOpen(false);
                            }}
                            style={{ padding: "8px 18px", borderRadius: "var(--radius)", border: "none", background: "var(--primary)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        </form>
      </div>

      {/* Jobs Table */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow-sm)",
        minWidth: 0, width: "100%",
      }}>

        {/* Table header */}
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid var(--border)",
          background: "rgba(15,61,62,0.03)", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 4, height: 18, borderRadius: 2, background: "#0F3D3E", flexShrink: 0 }} />
            <span style={{ fontFamily: "var(--font-display)", fontSize: 14, color: "#0F3D3E", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <BarChart2 size={15} strokeWidth={2} /> Report History
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--border)", padding: "2px 8px", borderRadius: 20 }}>
              Auto-clears after 24h
            </span>
          </div>

          {/* Active filter chips */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {typeFilter && (() => {
              const rt = REPORT_TYPES.find(r => r.value === typeFilter);
              const Icon = rt?.Icon;
              return (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "rgba(15,61,62,0.08)", color: "#0F3D3E", border: "1px solid rgba(15,61,62,0.2)" }}>
                  {Icon && <Icon size={10} strokeWidth={2.5} />} {rt?.label}
                  <button onClick={() => setTypeFilter("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#0F3D3E", fontSize: 11, padding: 0, lineHeight: 1, display: "flex", alignItems: "center" }}>
                    <X size={10} strokeWidth={3} />
                  </button>
                </span>
              );
            })()}
            {statusFilter && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: STATUS_META[statusFilter]?.bg, color: STATUS_META[statusFilter]?.color, border: `1px solid ${STATUS_META[statusFilter]?.border}` }}>
                {STATUS_DISPLAY[statusFilter] || statusFilter}
                <button onClick={() => setStatusFilter("")} style={{ background: "none", border: "none", cursor: "pointer", color: STATUS_META[statusFilter]?.color, fontSize: 11, padding: 0, lineHeight: 1, display: "flex", alignItems: "center" }}>
                  <X size={10} strokeWidth={3} />
                </button>
              </span>
            )}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg-input)", borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "11px 16px", textAlign: "left", whiteSpace: "nowrap" }}>
                  <SortHeader label="Created" field="createdAt" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </th>
                <th style={{ padding: "11px 16px", textAlign: "left" }}>
                  <FilterHeader label="Report Type" value={typeFilter} onChange={setTypeFilter} options={typeOptions} />
                </th>
                <th style={{ padding: "11px 16px", textAlign: "left", whiteSpace: "nowrap" }}>
                  <SortHeader label="Date Range" field="dateRange" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </th>
                <th style={{ padding: "11px 16px", textAlign: "left" }}>
                  <FilterHeader label="Job Status" value={statusFilter} onChange={setStatusFilter} options={jobStatusOptions} />
                </th>
                <th style={{ padding: "11px 16px", textAlign: "left" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Progress</span>
                </th>
                <th style={{ padding: "11px 16px", textAlign: "left" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Rows</span>
                </th>
                {isSuperAdmin && (
                  <th style={{ padding: "11px 16px", textAlign: "left" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>By</span>
                  </th>
                )}
                <th style={{ padding: "11px 16px", textAlign: "left" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Expires</span>
                </th>
                <th style={{ padding: "11px 16px", textAlign: "left" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Download</span>
                </th>
                <th style={{ padding: "11px 16px", textAlign: "left" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Delete</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} style={{ padding: "12px 16px" }}>
                        <div style={{ height: 14, borderRadius: 4, background: "var(--border)", width: `${50 + (j * 13) % 40}%`, animation: "pulse 1.5s ease-in-out infinite" }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : visibleJobs.length === 0 ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 10 : 9} style={{ padding: "48px 20px", textAlign: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                      <BarChart2 size={32} strokeWidth={1.5} color="var(--text-muted)" />
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)" }}>No report jobs found</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {typeFilter || statusFilter
                          ? "Try clearing column filters above."
                          : isSuperAdmin ? "No jobs match." : "Generate your first report above."}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : visibleJobs.map(job => {
                const isActive   = job.jobStatus === "QUEUED" || job.jobStatus === "PROCESSING";
                const expiry     = timeUntilExpiry(job.createdAt);
                const nearExpiry = expiry && expiry !== "Expired" &&
                  (Date.now() - new Date(job.createdAt).getTime()) > (20 * 60 * 60 * 1000);

                return (
                  <tr key={job.id} style={{
                    borderBottom: "1px solid var(--border)",
                    background: isActive ? "rgba(37,99,235,0.03)" : "transparent",
                    transition: "background 0.15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--bg-input)"}
                    onMouseLeave={e => e.currentTarget.style.background = isActive ? "rgba(37,99,235,0.03)" : "transparent"}
                  >
                    {/* Created */}
                    <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "monospace" }}>
                        {formatTimestamp(job.createdAt)}
                      </span>
                    </td>

                    {/* Report Type */}
                    <td style={{ padding: "11px 16px" }}>
                      <TypeBadge type={job.reportType} />
                    </td>

                    {/* Date Range */}
                    <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
                      {job.fromDate && job.toDate ? (
                        <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "monospace" }}>
                          {job.fromDate} → {job.toDate}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>All time</span>
                      )}
                    </td>

                    {/* Job Status */}
                    <td style={{ padding: "11px 16px" }}>
                      <StatusBadge status={job.jobStatus} />
                    </td>

                    {/* Progress */}
                    <td style={{ padding: "11px 16px", minWidth: 100 }}>
                      <ProgressBar pct={job.progressPercent} status={job.jobStatus} />
                      <div style={{ fontSize: 10, color: "var(--text-muted)", textAlign: "right", marginTop: 3, fontFamily: "monospace" }}>
                        {job.progressPercent ?? 0}%
                      </div>
                    </td>

                    {/* Rows */}
                    <td style={{ padding: "11px 16px", fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
                      {job.rowCount ? job.rowCount.toLocaleString() : "—"}
                    </td>

                    {/* By */}
                    {isSuperAdmin && (
                      <td style={{ padding: "11px 16px", fontSize: 12, color: "var(--text-secondary)" }}>
                        {job.requestedBy || "—"}
                      </td>
                    )}

                    {/* Expires */}
                    <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
                      {job.jobStatus === "DONE" || job.jobStatus === "FAILED" ? (
                        <span style={{ fontSize: 11, fontWeight: 600, color: nearExpiry ? "#d97706" : "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {nearExpiry && <AlertTriangle size={11} strokeWidth={2.5} color="#d97706" />}
                          {expiry}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: "var(--text-muted)", opacity: 0.5 }}>—</span>
                      )}
                    </td>

                    {/* Download */}
                    <td style={{ padding: "11px 16px" }}>
                      {job.jobStatus === "DONE" && job.downloadUrl ? (
                        <button
                          onClick={() => handleDownload(job)}
                          disabled={downloadingId === job.id}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "5px 12px", borderRadius: "var(--radius)",
                            background: downloadingId === job.id ? "rgba(5,150,105,0.05)" : "rgba(5,150,105,0.08)",
                            border: "1px solid rgba(5,150,105,0.2)",
                            color: "#059669", fontSize: 11, fontWeight: 600,
                            cursor: downloadingId === job.id ? "not-allowed" : "pointer",
                            whiteSpace: "nowrap", transition: "background 0.15s",
                            opacity: downloadingId === job.id ? 0.6 : 1,
                          }}
                          onMouseEnter={e => { if (downloadingId !== job.id) e.currentTarget.style.background = "rgba(5,150,105,0.15)"; }}
                          onMouseLeave={e => { if (downloadingId !== job.id) e.currentTarget.style.background = "rgba(5,150,105,0.08)"; }}
                        >
                          {downloadingId === job.id ? (
                            <><Loader2 size={11} strokeWidth={2.5} style={{ animation: "spin 1s linear infinite" }} /> Downloading…</>
                          ) : (
                            <><Download size={11} strokeWidth={2.5} /> Download</>
                          )}
                        </button>
                      ) : isActive ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "#2563eb", fontWeight: 500 }}>
                          <Loader2 size={12} strokeWidth={2.5} style={{ animation: "spin 1s linear infinite" }} />
                          In progress
                        </span>
                      ) : job.jobStatus === "FAILED" ? (
                        <span style={{ fontSize: 11, color: "#dc2626", maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={job.errorMessage}>
                          {job.errorMessage || "Failed"}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: "var(--text-muted)", opacity: 0.5 }}>—</span>
                      )}
                    </td>

                    {/* Delete */}
                    <td style={{ padding: "11px 16px" }}>
                      <button
                        onClick={() => handleDelete(job.id)}
                        style={{
                          padding: "5px 10px", borderRadius: "var(--radius)",
                          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
                          color: "#dc2626", fontSize: 11, fontWeight: 600, cursor: "pointer",
                          transition: "background 0.18s", display: "inline-flex", alignItems: "center", gap: 5,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.15)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.08)"}
                        title={isActive ? "Force-delete stuck job" : "Delete this report"}
                      >
                        <Trash2 size={11} strokeWidth={2} /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {!loading && visibleJobs.length > 0 && (
          <div style={{
            padding: "12px 20px", borderTop: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "var(--bg-input)",
          }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Showing <strong style={{ color: "var(--text-primary)" }}>{visibleJobs.length}</strong> of{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {(isSuperAdmin ? totalElements : jobs.length).toLocaleString()}
              </strong> jobs
            </span>

            {isSuperAdmin && totalPages > 1 && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0}
                  style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "none", cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.35 : 1, fontSize: 13, color: "var(--text-secondary)" }}
                >‹</button>
                {Array.from({ length: totalPages }, (_, i) => i)
                  .filter(i => Math.abs(i - page) <= 2)
                  .map(i => (
                    <button key={i} onClick={() => handlePageChange(i)} style={{ padding: "4px 9px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px solid var(--border)", background: i === page ? "#0F3D3E" : "none", color: i === page ? "#fff" : "var(--text-secondary)", transition: "all 0.15s" }}>
                      {i + 1}
                    </button>
                  ))}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages - 1}
                  style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "none", cursor: page === totalPages - 1 ? "not-allowed" : "pointer", opacity: page === totalPages - 1 ? 0.35 : 1, fontSize: 13, color: "var(--text-secondary)" }}
                >›</button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin  { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}